import React, { useEffect } from "react";
import { WizardFormField } from "../machines/immersive-wizard.machine";
import { WizardFormData } from "../types/wizard.types";
import { X } from "lucide-react";
import { Button } from "@pushpress/pushpress-ui";

interface StaffAppStepProps {
  formData: WizardFormData;
  onFormUpdate: (data: Partial<WizardFormData>) => void;
  onValidationChange: (isValid: boolean) => void;
}

const StaffAppStep: React.FC<StaffAppStepProps> = ({
  formData,
  onFormUpdate,
  onValidationChange,
}) => {
  const hasValidStaff = formData[WizardFormField.StaffMembers].some(
    (member) =>
      member.firstName.trim() && member.lastName.trim() && member.email.trim(),
  );

  useEffect(() => {
    onValidationChange(hasValidStaff);
  }, [hasValidStaff, onValidationChange]);

  return (
    <div>
      <div className="tw-space-y-3 tw-mb-4">
        {formData[WizardFormField.StaffMembers].map((staff, index) => (
          <div key={index} className="tw-flex tw-gap-3 tw-items-center">
            <input
              type="text"
              placeholder="First name"
              value={staff.firstName}
              onChange={(e) => {
                const updatedStaff = [
                  ...formData[WizardFormField.StaffMembers],
                ];
                updatedStaff[index] = {
                  ...updatedStaff[index],
                  firstName: e.target.value,
                };
                onFormUpdate({
                  [WizardFormField.StaffMembers]: updatedStaff,
                });
              }}
              className="tw-flex-1 tw-px-3 tw-py-2 tw-bg-neutral-800/10 tw-border tw-border-neutral-800/20 tw-rounded-lg tw-text-neutral-800 tw-placeholder-neutral-800/60 tw-outline-none focus:tw-border-neutral-800/40"
            />
            <input
              type="text"
              placeholder="Last name"
              value={staff.lastName}
              onChange={(e) => {
                const updatedStaff = [
                  ...formData[WizardFormField.StaffMembers],
                ];
                updatedStaff[index] = {
                  ...updatedStaff[index],
                  lastName: e.target.value,
                };
                onFormUpdate({
                  [WizardFormField.StaffMembers]: updatedStaff,
                });
              }}
              className="tw-flex-1 tw-px-3 tw-py-2 tw-bg-neutral-800/10 tw-border tw-border-neutral-800/20 tw-rounded-lg tw-text-neutral-800 tw-placeholder-neutral-800/60 tw-outline-none focus:tw-border-neutral-800/40"
            />
            <input
              type="email"
              placeholder="Email"
              value={staff.email}
              onChange={(e) => {
                const updatedStaff = [
                  ...formData[WizardFormField.StaffMembers],
                ];
                updatedStaff[index] = {
                  ...updatedStaff[index],
                  email: e.target.value,
                };
                onFormUpdate({
                  [WizardFormField.StaffMembers]: updatedStaff,
                });
              }}
              className="tw-flex-1 tw-px-3 tw-py-2 tw-bg-neutral-800/10 tw-border tw-border-neutral-800/20 tw-rounded-lg tw-text-neutral-800 tw-placeholder-neutral-800/60 tw-outline-none focus:tw-border-neutral-800/40"
            />
            {index >= 1 && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  const updatedStaff = formData[
                    WizardFormField.StaffMembers
                  ].filter((_, i) => i !== index);
                  onFormUpdate({
                    [WizardFormField.StaffMembers]: updatedStaff,
                  });
                }}
              >
                <X className="tw-w-4 tw-h-4" />
              </Button>
            )}
          </div>
        ))}
      </div>

      <div className="tw-flex tw-gap-3">
        <button
          onClick={() => {
            const updatedStaff = [
              ...formData[WizardFormField.StaffMembers],
              { firstName: "", lastName: "", email: "" },
            ];
            onFormUpdate({
              [WizardFormField.StaffMembers]: updatedStaff,
            });
          }}
          className="tw-px-4 tw-py-2 tw-border tw-border-dashed tw-border-neutral-800/30 tw-rounded-lg tw-text-neutral-800/70 hover:tw-border-neutral-800/50 hover:tw-text-neutral-800 tw-transition-all tw-flex tw-items-center tw-gap-2"
        >
          <span className="tw-text-lg">+</span>
          Add another staff member
        </button>

        <button
          onClick={() => {
            // Here you would typically send the invites
            // For now, just show an alert
            const validStaff = formData[WizardFormField.StaffMembers].filter(
              (s) => s.firstName && s.lastName && s.email,
            );
            if (validStaff.length > 0) {
              alert(
                `Invites would be sent to ${validStaff.length} staff member(s)`,
              );
            } else {
              alert("Please fill in at least one complete staff member");
            }
          }}
          className="tw-px-4 tw-py-2 tw-bg-neutral-800 tw-text-white tw-rounded-lg hover:tw-bg-neutral-800/90 tw-transition-all"
        >
          Send Invites
        </button>
      </div>
    </div>
  );
};

export default StaffAppStep;
